import {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {useSnackbar} from 'notistack';

import {useCreateBanner} from '@entities/banner';
import {useUploadAdminFile} from '@entities/common';
import {CardBox} from '@shared/components';
import {ROUTE__BANNERS} from '@shared/constants/routes';
import {createEmptyTranslationDict, resolveEnglishText} from '@shared/helpers';
import {TranslationDict} from '@shared/types';
import {FormRow, Header} from '@widgets/common';
import {FormFields} from './FormFields';
import {FormValues, v8nSchema} from './v8nSchema';

import {COLOR__WHITE} from '@/theme/colors';

export const CreateBannerPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const {mutateAsync: createBanner, isPending: isCreating} = useCreateBanner();
  const {mutateAsync: uploadAdminFile, isPending: isUploading} =
    useUploadAdminFile();

  const [nameTranslations, setNameTranslations] = useState<TranslationDict>(
    createEmptyTranslationDict,
  );

  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      name: '',
      logoFile: null,
      appUrl: '',
      webUrl: '',
      activeFrom: null,
      activeTo: null,
      geos: [],
      order: 0,
    },
  });
  const {
    handleSubmit,
    formState: {dirtyFields},
  } = formMethods;

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      const uploadedLogo = values.logoFile
        ? await uploadAdminFile({file: values.logoFile, type: 'category_logo'})
        : null;

      if (!uploadedLogo) {
        enqueueSnackbar('Banner image is required', {variant: 'error'});
        return;
      }

      await createBanner({
        nameTrans: {
          ...nameTranslations,
          en: resolveEnglishText(
            nameTranslations,
            values.name,
            !!dirtyFields.name,
          ),
        },
        logoUrl: uploadedLogo.key,
        appUrl: values.appUrl || null,
        webUrl: values.webUrl || null,
        activeFrom: values.activeFrom,
        activeTo: values.activeTo,
        geos: values.geos.map(g => g.id),
        order: values.order,
      });

      enqueueSnackbar('Banner created', {variant: 'success'});
      navigate(ROUTE__BANNERS);
    } catch {
      enqueueSnackbar('Failed to create banner', {variant: 'error'});
    }
  };

  return (
    <>
      <Header title="Create banner" mb={1} />

      <CardBox bgcolor={COLOR__WHITE} maxWidth={600}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormProvider {...formMethods}>
            <FormFields
              nameTranslations={nameTranslations}
              onNameTranslationsChange={setNameTranslations}
            />
          </FormProvider>

          <FormRow width={420} mt={3}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={isCreating || isUploading}
              sx={{flex: 2}}
            >
              Create banner
            </Button>
          </FormRow>
        </form>
      </CardBox>
    </>
  );
};
